import { FC, useEffect, useState, useRef, useMemo } from "react";
import {
  NextPlan,
  ArrowCircleRight,
  Cancel,
  ReplayCircleFilled,
  AccountBalanceWallet,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ITrayPrintRequest, ITrayPrintItem } from "../../../typings/interfaces/print/ticket-print-request";
import dayjs, { Dayjs } from "dayjs";
import { v4 as uuidv4 } from "uuid";

import CheckInSummary from "./checkinSummary";
import Payment from "./payment";
import Success from "./success";
import Failure from "./failure";
import CustomModal from "../../common/custom-modal/custom-modal";
import CashChangeModal from "./CashChangeModal";
import PaymentErrorModal, { IPaymentErrorModalProps } from "./PaymentErrorModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Colors } from "../../../configs";
import { PaymentModalContents } from "../../../typings/enum/paymentModalContents";
import {
  addBillNoteAction,
  addSession,
  editBillAction,
  getBillItemIdListAction,
  newBillAction,
  newPaymentAction,
} from "../../../redux/actions/cartAction";
import { getCustomerByIdAction } from "../../../redux/actions/customerAction";
import { getCustomerOutstandingAction } from "../../../redux/actions/customerInsightsAction";
import { getTenantId, formatUSDate, formatUS12HourTime } from "../../../util/common-functions";
import { buildCustomizationLines, customerStatusLetter, formatFullName, formatGarmentColor, isDeferredPayment, paymentStatusLetter, printedItemPrice, resolvePinTagAlias, toDisplayCase } from "../../../util/print-formatting";
import { getAllColorPinTagsAction } from "../../../redux/actions/pintagAction";
import useCart from "../../../hooks/useCart";
import useTrayPrinter from "../../../hooks/useTrayPrinter";
import { useTranslation } from "../../../util/translation-provider";
import getCheckOutDate from "../../../configs/checkoutDateRules";
import { PaymentTypeEnum } from "../../../typings/enum/paymentTypes";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import { DiscountSliceAction } from "../../../redux/slice/discountSlice";
import { IPaymentRequest } from "../../../typings/interfaces/requests";
import { PaymentCreateResponse } from "../../../typings/interfaces/responses/paymentResponse";
import { BaseResponseSingle } from "../../../typings";
import { IHalfPayment } from "../../../typings/interfaces/payment/halfPayment";
import { ICartSession } from "../../../typings/interfaces/pos/cart-session";
import { ActionTileModal } from "../../atoms/action-tile-modal/action-tile-modal";
import { PrintCopyChoice } from "./CashChangeModal";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { IBillResponse } from "../../../typings/interfaces/responses/new-bill-response";
import { store } from "../../../redux/store";
import { useSpecialDate } from "../../../hooks/useSpecialDate";

export interface IPaymentModalData {
  paymentMode: PaymentTypeEnum;
  paidAmount: number;
  // The amount actually typed/keyed into the Cash "Paid" field, BEFORE
  // payment.tsx's handleOnCheckout folds `trackrem` (the bill's already-
  // collected amount, so the backend receives a total that lets it settle
  // the bill correctly) into `paidAmount`. Callers that show the cashier a
  // "Cash Received"/change-due confirmation (CashChangeModal) need THIS
  // value, not the trackrem-inflated one, or the displayed change is off by
  // whatever was already paid on the bill before this session - the actual
  // charge itself is unaffected (capped at the real due elsewhere), only
  // the confirmation figure was wrong. Optional so existing callers that
  // don't care keep working unchanged.
  rawPaidAmount?: number;
  checkNumber?: string;
  isStoreCredit?: boolean;
}

export interface IBillPayment {
  billId: number;
  billIdWithCode: string;
  billTotal: number;
  paidAmount: number;
}

export interface IPaymentModalProps {
  discount: number;
  total: number;
  subTotal?: number;
  open: boolean;
  onClose(): void;
  // onCheckout(data: IPaymentModalData): void;
  content: PaymentModalContents;
}

export interface ModalActionItem {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  color: string;
}

const PaymentModal: FC<IPaymentModalProps> = ({
  open,
  onClose,
  discount,
  total,
}) => {
  const [openActionTileModal, setOpenActionTileModal] = useState(false);
  const [modalActionItems, setModalActionItems] = useState<ModalActionItem[]>(
    []
  );
  const [pendingPrintRequests, setPendingPrintRequests] = useState<ITrayPrintRequest[]>([]);
  const [printingCopy, setPrintingCopy] = useState<PrintCopyChoice | null>(null);
  const [printFeedback, setPrintFeedback] = useState<{
    copy: PrintCopyChoice;
    success: boolean;
    message?: string;
  } | null>(null);
  const [isCheckoutTriggered, setIsCheckoutTriggered] = useState(false);
  const [showCashChangeModal, setShowCashChangeModal] = useState(false);
  const [cashChangeData, setCashChangeData] = useState<{
    paymentType?: PaymentTypeEnum;
    billAmount?: number;
    tenderedAmount?: number;
    changeAmount?: number;
    isStoreCredit?: boolean;
    checkNumber?: string;
    outstandingAmount?: number;
    dueDate?: string;
    previousStoreCreditBalance?: number;
    customerName?: string;
    customerId?: number;
    invoiceId?: string;
  } | null>(null);
  // Refs to hold cash tendered / bill total computed on the FE at checkout time.
  // The backend never receives the tendered amount so we can't rely on its response.
  const cashTenderedRef = useRef(0);
  const cashBillRef = useRef(0);
  const checkNumberRef = useRef<string | undefined>(undefined);
  const storeCreditBalanceBeforeRef = useRef(0);
  const billTotalForErrorRef = useRef(0);
  // Snapshot of the bills newBillAction just returned, and the cart sessions
  // that produced them, captured the instant the create call resolves - not
  // read back later from redux `bills`/`cart`. The isPaymentComplete effect
  // below used to read those selectors directly, several renders and
  // dispatches after creation (clearSession/addSession run in that same
  // completion effect, right after this snapshot is taken), and ended up
  // building zero print requests with none of buildPrintRequestForBill's own
  // fallbacks ever triggering - meaning `bills` itself, not any one lookup,
  // was already empty/stale by the time it was read. A ref set at the one
  // moment this data is unambiguously fresh sidesteps that entirely.
  const justCreatedBillsRef = useRef<IBillResponse[] | null>(null);
  const justCreatedSessionsRef = useRef<ICartSession[] | null>(null);
  const [showPaymentErrorModal, setShowPaymentErrorModal] = useState(false);
  const [paymentErrorData, setPaymentErrorData] = useState<Omit<IPaymentErrorModalProps, "open" | "onClose" | "onRetry"> | null>(null);
  const [intiaLRender, setIntiaLRender] = useState(true);
  const navigate = useNavigate();
  const { billsError, bills, selectedSession } = useAppSelector(
    (state) => state.cart
  );
  const dispatch = useAppDispatch();
  // Needed to resolve the pin tag's 2-letter alias for printing
  // (resolvePinTagAlias) - nothing else in this modal's flow loads the pin
  // tag color catalog, so without this the alias lookup would find nothing
  // by the time buildPrintRequestForBill runs.
  useEffect(() => {
    if (open && !store.getState().pintag.getColorPintags?.body?.length) {
      dispatch(getAllColorPinTagsAction());
    }
  }, [open, dispatch]);
  const modalShownRef = useRef(false);
  const getStoreConfigResponse = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );
  const hasMounted = useRef(false);

  useEffect(() => {
    if (open && !hasMounted.current) {
      modalShownRef.current = false;
      hasMounted.current = true;
      setIntiaLRender(true);
    }
    if (open) {
      // No default payment method - the client asked that the cashier always
      // have to make a conscious choice here instead of being able to just
      // continue with whatever was pre-selected. EXCEPTION: a monthly-charge
      // customer only ever has Monthly enabled (see isMonthlyDisabled in
      // payment.tsx), so there's no real choice to make - pre-select it.
      const isMonthlyCustomer = (customer?.customerPayAtPref ?? []).includes(
        "MONTHLY_CHARGE"
      );
      setPaymentType(isMonthlyCustomer ? PaymentTypeEnum.MONTHLY : null);
    }
  }, [open]);

  const tenantId = getTenantId();
  const {
    cartTotal,
    totalDiscountAmount,
    getSessionDiscountById,
    getSessionTotalBySessionId,
  } = useCart();
  const { printTicket } = useTrayPrinter();
  const translation = useTranslation();
  const { customer } = useAppSelector((state) => state.customer);
  // Last known non-null customer. updateHalfOrFullPayment refreshes the
  // customer (for their post-payment store-credit balance) by dispatching
  // getCustomerByIdAction right in the SAME synchronous block as the
  // paymentHalfDetails update that's this effect's own dependency -
  // getCustomerByIdAction.pending sets state.customer.customer to null for
  // the GET's duration, and both dispatches land in the same React batch,
  // so the isPaymentComplete effect below deterministically saw customer
  // === null on the exact render it needed it, every single time. This was
  // the real reason the print snapshot kept coming back empty even after
  // the bills/cart ref fix (confirmed via a "customer: missing" console
  // line) - `customer` itself goes stale, not just bills.
  const customerRef = useRef<typeof customer>(null);
  useEffect(() => {
    if (customer) customerRef.current = customer;
  }, [customer]);
  const { cart, paymentHalfDetails } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);
  // const billTotal = cartTotal - totalDiscountAmount;
  const totalBillAmountFromHalfPayment = paymentHalfDetails.reduce(
    (acc, item) => acc + item.fullpaymentAmount,
    0
  );
  const paidAmount = paymentHalfDetails.reduce(
    (acc, item) => acc + item.halfPaymentAmount,
    0
  );
  const snackbar = useSnackbarContext();
  const params = useParams();

  const [masterSwitch, setMasterSwitch] = useState(false);
  const [sessionSwitches, setSessionSwitches] = useState<
    { id: string; checked: boolean }[]
  >([]);
  const userId = Number(params.id);
  const billId = params.billId;

  const customerId = params.id;

  const successActionItems = [
    {
      icon: <NextPlan />,
      label: "Go to Lobby",
      onClick: () => {
        navigate("/pos/lobby");
      },
      color: Colors.PRIMARY,
    },
    {
      icon: <ArrowCircleRight />,
      label: "Stay Here",
      onClick: () => {
        setOpenActionTileModal(false);
      },
      color: Colors.PRIMARY,
    },
    {
      icon: <NextPlan />,
      label: "Go to Pickup",
      onClick: () => {
        navigate(`/pos/customer/${customerId}/history`);
      },
      color: Colors.PRIMARY,
    },
  ];

  const failureActionItems = [
    {
      icon: <Cancel />,
      label: "Cancel",
      onClick: () => setOpenActionTileModal(false),
      color: Colors.BROWN,
    },
    {
      icon: <ReplayCircleFilled />,
      label: "Retry",
      onClick: () => console.log("Retry clicked"),
      color: Colors.BROWN,
    },
  ];

  const parseTime = (time: string) => {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);
    return date;
  };

  const defaultCutOffTime = "10:00:00";
  const defaultPickupTime = "15:00:00";
  const defaultWorkingDays = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const defaultRegularServiceDays = 2;
  const defaultSpecialServiceDays = 0;

  const checkInCutOffTimeDate = parseTime(
    getStoreConfigResponse?.body?.cutOffTime || defaultCutOffTime
  );
  const pickupTimeDate = parseTime(
    getStoreConfigResponse?.body?.pickupTime || defaultPickupTime
  );

  const [masterDate, setMasterDate] = useState<Dayjs | null>(null);
  const [masterTime, setMasterTime] = useState<Dayjs | null>(null);
  const [sessionDates, setSessionDates] = useState<
    { id: string; specialDate: Dayjs | null }[]
  >([]);
  const [sessionTimes, setSessionTimes] = useState<
    { id: string; specialTime: Dayjs | null }[]
  >([]);
  // No default - the cashier must actively pick a payment method every time.
  const [paymentType, setPaymentType] = useState<PaymentTypeEnum | null>(null);

  useEffect(() => {
    if (
      open &&
      customerId &&
      (paymentType === PaymentTypeEnum.MONTHLY ||
        paymentType === PaymentTypeEnum.LATER)
    ) {
      dispatch(getCustomerOutstandingAction(Number(customerId)));
    }
  }, [open, customerId, paymentType, dispatch]);

  const { date: globalSpecialDate } = useSpecialDate();

  useEffect(() => {
    setMasterDate(
      getCheckOutDate({
        isSpecialDay: masterSwitch,
        checkInCutOffTime: checkInCutOffTimeDate,
        pickupTime: pickupTimeDate,
        workingDays:
          getStoreConfigResponse?.body?.workingDays ?? defaultWorkingDays,
        generalServiceDays:
          getStoreConfigResponse?.body?.daysToDeliver ??
          defaultRegularServiceDays,
        specialServiceDays:
          getStoreConfigResponse?.body?.specialDaysToDeliver ??
          defaultSpecialServiceDays,
      })
    );
    setMasterTime(
      dayjs(
        new Date().setHours(
          getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
          getStoreConfigResponse?.body ? pickupTimeDate.getMinutes() : 0,
          0,
          0
        )
      )
    );
  }, [getStoreConfigResponse]);

  // `cart` gets a brand-new array reference (via cloneDeep in the `addSession`
  // thunk) on every dispatch, including ones that only touch an unrelated
  // session's pinTag - e.g. validating a pin tag in the Edit Pin Tag drawer
  // while this summary is open. Depending directly on `[cart]` here reset
  // every session's Special switch/date/time back to defaults on each of
  // those unrelated updates, which is what produced the "Special switch
  // flickering" back to off while a cashier was mid-edit elsewhere in the
  // same summary. Key off the actual session id set/order instead, and only
  // add defaults for genuinely new sessions - existing entries are preserved.
  const cartSessionIdsKey = useMemo(
    () => cart.map((item) => item.sessionId).join(","),
    [cart]
  );

  useEffect(() => {
    const cartSessionIds = cartSessionIdsKey
      ? cartSessionIdsKey.split(",")
      : [];

    setSessionSwitches((prev) =>
      cartSessionIds.map(
        (id) => prev.find((item) => item.id === id) ?? { id, checked: false }
      )
    );
    setSessionDates((prev) =>
      cartSessionIds.map(
        (id) =>
          prev.find((item) => item.id === id) ?? {
            id,
            specialDate: getCheckOutDate({
              isSpecialDay: false,
              checkInCutOffTime: checkInCutOffTimeDate,
              pickupTime: pickupTimeDate,
              workingDays:
                getStoreConfigResponse?.body?.workingDays ??
                defaultWorkingDays,
              generalServiceDays:
                getStoreConfigResponse?.body?.daysToDeliver ??
                defaultRegularServiceDays,
              specialServiceDays:
                getStoreConfigResponse?.body?.specialDaysToDeliver ??
                defaultSpecialServiceDays,
            }),
          }
      )
    );
    setSessionTimes((prev) =>
      cartSessionIds.map(
        (id) =>
          prev.find((item) => item.id === id) ?? {
            id,
            specialTime: dayjs(
              new Date().setHours(
                getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
                getStoreConfigResponse?.body
                  ? pickupTimeDate.getMinutes()
                  : 0,
                0,
                0
              )
            ),
          }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSessionIdsKey]);

  useEffect(() => {
    setSessionDates((prevSessionDates) => {
      return prevSessionDates.map((sessionDate) => {
        const sessionSwitch = sessionSwitches.find(
          (item) => item?.id === sessionDate?.id
        );
        if (sessionSwitch && sessionSwitch.checked && masterSwitch) {
          return {
            id: sessionDate?.id,
            specialDate: dayjs(masterDate),
          };
        }
        if (sessionSwitch && sessionSwitch.checked && !masterSwitch) {
          return {
            id: sessionDate?.id,
            specialDate: getCheckOutDate({
              isSpecialDay: true,
              checkInCutOffTime: checkInCutOffTimeDate,
              pickupTime: pickupTimeDate,
              workingDays:
                getStoreConfigResponse?.body?.workingDays ?? defaultWorkingDays,
              generalServiceDays:
                getStoreConfigResponse?.body?.daysToDeliver ??
                defaultRegularServiceDays,
              specialServiceDays:
                getStoreConfigResponse?.body?.specialDaysToDeliver ??
                defaultSpecialServiceDays,
            }),
          };
        }
        if (sessionSwitch && !sessionSwitch.checked && masterSwitch) {
          return {
            id: sessionDate?.id,
            specialDate: getCheckOutDate({
              isSpecialDay: false,
              checkInCutOffTime: checkInCutOffTimeDate,
              pickupTime: pickupTimeDate,
              workingDays:
                getStoreConfigResponse?.body?.workingDays ?? defaultWorkingDays,
              generalServiceDays:
                getStoreConfigResponse?.body?.daysToDeliver ??
                defaultRegularServiceDays,
              specialServiceDays:
                getStoreConfigResponse?.body?.specialDaysToDeliver ??
                defaultSpecialServiceDays,
            }),
          };
        }
        if (sessionSwitch && !sessionSwitch.checked && !masterSwitch) {
          return {
            id: sessionDate?.id,
            specialDate: getCheckOutDate({
              isSpecialDay: false,
              checkInCutOffTime: checkInCutOffTimeDate,
              pickupTime: pickupTimeDate,
              workingDays:
                getStoreConfigResponse?.body?.workingDays ?? defaultWorkingDays,
              generalServiceDays:
                getStoreConfigResponse?.body?.daysToDeliver ??
                defaultRegularServiceDays,
              specialServiceDays:
                getStoreConfigResponse?.body?.specialDaysToDeliver ??
                defaultSpecialServiceDays,
            }),
          };
        }

        return sessionDate;
      });
    });
    setSessionTimes((prevSessionTimes) => {
      return prevSessionTimes.map((sessionTime) => {
        const sessionSwitch = sessionSwitches.find(
          (item) => item?.id === sessionTime?.id
        );

        if (sessionSwitch && sessionSwitch.checked && masterSwitch) {
          return {
            id: sessionTime?.id,
            specialTime: dayjs(masterTime),
          };
        }
        if (sessionSwitch && sessionSwitch.checked && !masterSwitch) {
          return {
            id: sessionTime?.id,
            specialTime: dayjs(
              new Date().setHours(
                getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
                getStoreConfigResponse?.body ? pickupTimeDate.getMinutes() : 0,
                0,
                0
              )
            ),
          };
        }
        if (sessionSwitch && !sessionSwitch.checked && masterSwitch) {
          return {
            id: sessionTime?.id,
            specialTime: dayjs(
              new Date().setHours(
                getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
                getStoreConfigResponse?.body ? pickupTimeDate.getMinutes() : 0,
                0,
                0
              )
            ),
          };
        }
        if (sessionSwitch && !sessionSwitch.checked && !masterSwitch) {
          return {
            id: sessionTime?.id,
            specialTime: dayjs(
              new Date().setHours(
                getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
                getStoreConfigResponse?.body ? pickupTimeDate.getMinutes() : 0,
                0,
                0
              )
            ),
          };
        }

        return sessionTime;
      });
    });
  }, [masterSwitch, masterDate, masterTime, cart]);

  const handleOnChangeMasterSwitch = () => {
    setMasterSwitch((prevState) => {
      const newSwitches = JSON.parse(JSON.stringify(sessionSwitches));
      newSwitches.forEach((item: any) => {
        item.checked = !prevState;
      });
      setSessionSwitches(newSwitches);
      const date = new Date();
      date.setHours(
        getStoreConfigResponse?.body ? pickupTimeDate.getHours() : 15,
        getStoreConfigResponse?.body ? pickupTimeDate.getMinutes() : 0,
        0,
        0
      );
      setMasterTime(dayjs(date));
      return !prevState;
    });
    const checkInCutOffTimeString =
      getStoreConfigResponse?.body?.cutOffTime || "10:00:00";
    const pickupTimeString =
      getStoreConfigResponse?.body?.pickupTime || "15:00:00";

    const [
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
    ] = checkInCutOffTimeString.split(":").map(Number);
    const checkInCutOffTimeDate = new Date();
    checkInCutOffTimeDate.setHours(
      checkInCutOffTimeHours,
      checkInCutOffTimeMinutes,
      checkInCutOffTimeSeconds,
      0
    );

    const [pickupTimeHours, pickupTimeMinutes, pickupTimeSeconds] =
      pickupTimeString.split(":").map(Number);
    const pickupTimeDate = new Date();
    pickupTimeDate.setHours(
      pickupTimeHours,
      pickupTimeMinutes,
      pickupTimeSeconds,
      0
    );

    const date = {
      isSpecialDay: !masterSwitch,
      checkInCutOffTime: checkInCutOffTimeDate,
      pickupTime: pickupTimeDate,
      workingDays:
        getStoreConfigResponse?.body?.workingDays ?? defaultWorkingDays,
      generalServiceDays:
        getStoreConfigResponse?.body?.daysToDeliver ??
        defaultRegularServiceDays,
      specialServiceDays:
        getStoreConfigResponse?.body?.specialDaysToDeliver ??
        defaultSpecialServiceDays,
    };
    setMasterDate(getCheckOutDate(date));
  };
  const handleOnChangeMasterDate = (date: Dayjs | null) => {
    setMasterDate(date);
  };
  const handleOnChangeMasterTime = (time: Dayjs | null) => {
    setMasterTime(time);
  };

  const handleOnChangeSessionSwitch = (id: string) => {
    const newSwitches = JSON.parse(JSON.stringify(sessionSwitches));
    const index = newSwitches.findIndex((item: any) => item?.id === id);
    newSwitches[index].checked = !newSwitches[index].checked;
    setSessionSwitches(newSwitches);
  };
  // JSON.parse(JSON.stringify(...)) previously "cloned" these arrays, but
  // Dayjs implements toJSON() (-> toISOString()), so that round-trip
  // silently turned EVERY session's specialDate/specialTime into a plain
  // string, not just the one being updated here. With one session that's
  // invisible (there's nothing else in the array to corrupt), but with a
  // multi-session checkout every other session's date/time got permanently
  // downgraded to a string, and later code calling `.format()` on it
  // directly (buildPrintRequestForBill) crashed with "... is not a
  // function". `.map` keeps every untouched entry's real Dayjs object intact.
  const handleOnChangeSessionDate = (id: string, date: Dayjs | null) => {
    setSessionDates((prev) =>
      prev.map((item) => (item.id === id ? { ...item, specialDate: date } : item))
    );
  };
  const handleOnChangeSessionTime = (id: string, time: Dayjs | null) => {
    setSessionTimes((prev) =>
      prev.map((item) => (item.id === id ? { ...item, specialTime: time } : item))
    );
  };

  const [content, setContent] = useState<PaymentModalContents>(
    PaymentModalContents.SUMMARY
  );

  const buildPrintRequestForBill = (
    bill: IBillResponse,
    halfPayment: IHalfPayment,
    copyType: "receipt" | "storeCopy"
  ): ITrayPrintRequest | null => {
    const printCustomer = customerRef.current ?? customer;
    if (!printCustomer) return null;
    // Prefer the just-created-sessions ref (captured the instant
    // newBillAction resolved - see its own comment for why redux `cart`
    // alone was found unreliable here), then the live `cart` selector as a
    // second attempt, then fall back to parsing billString (the same source
    // reprint-modal.tsx/ticket-print.ts relies on for a previously-settled
    // bill) for the case where the session isn't in either - e.g. a payment
    // made against an older/edited bill rather than one just created. Only
    // when ALL sources miss do we drop the request.
    const sessionsForLookup = justCreatedSessionsRef.current ?? cart;
    let session: ICartSession | undefined = sessionsForLookup.find(
      (s) => s.sessionId === bill.sessionId
    );
    if (!session?.cartItems?.length) {
      session = cart.find((s) => s.sessionId === bill.sessionId);
    }
    if (!session?.cartItems?.length) {
      try {
        session = JSON.parse(bill.billString) as ICartSession;
      } catch {
        session = undefined;
      }
    }
    if (!session?.cartItems?.length) {
      // Neither source had items - leave a breadcrumb, since the caller
      // drops this bill's print request silently from here on (with the
      // cashier only seeing "Nothing to print", not why).
      console.warn(
        `[payment-modal] No printable session found for bill ${bill.id} (sessionId: ${bill.sessionId}) - cart had no match and billString was empty/unparseable.`
      );
      return null;
    }

    const sessionDate = sessionDates.find((d) => d.id === bill.sessionId);
    const sessionTime = sessionTimes.find((t) => t.id === bill.sessionId);
    const now = new Date();

    const pickupAfter = sessionTime?.specialTime
      ? sessionTime.specialTime.format("h:mm A")
      : `${pickupTimeDate.getHours() % 12 || 12}:${String(pickupTimeDate.getMinutes()).padStart(2, "0")} ${pickupTimeDate.getHours() >= 12 ? "PM" : "AM"}`;

    const items: ITrayPrintItem[] = session.cartItems.map((item) => ({
      qty: item.qty,
      description:
        [
          item.garmentType?.name && toDisplayCase(item.garmentType.name),
          item.garmentColor?.map((c) => formatGarmentColor(String(c))).join(", "),
          item.materialType?.name && toDisplayCase(item.materialType.name),
          item.garmentStyle?.name && toDisplayCase(item.garmentStyle.name),
        ]
          .filter(Boolean)
          .join(" – ") ||
        (item.serviceType?.categoryName && toDisplayCase(item.serviceType.categoryName)) ||
        "Item",
      price: printedItemPrice(item),
      note: item.note ?? undefined,
      customizations: buildCustomizationLines(item),
    }));

    // Bill-level notes: whichever ones already made it to the server for
    // this bill, plus any still sitting as `temporaryNotes` for this
    // checkout's session (the checkout flow above persists those via
    // addBillNoteAction, but that happens async/after this print request is
    // built, so they wouldn't show up in `persistedBillNotes` yet).
    const notes = [
      ...(persistedBillNotes ?? [])
        .filter((n) => n.billId === bill.id)
        .map((n) => n.note),
      ...temporaryNotes
        .filter((n) => n.sessionId === bill.sessionId)
        .map((n) => n.note),
    ];

    const subtotal = halfPayment.fullpaymentAmount;
    const discount = bill.discountAmount ?? 0;
    // Printed Total is the post-discount amount (Subtotal - Discount, both
    // still shown as their own rows above it), and Balance reconciles that
    // against what's actually been paid.
    const total = subtotal - discount;
    const paidBy = halfPayment.payments[0]?.paymentType ?? "";
    // For CASH, the customer can tender more than the bill and get change
    // back - halfPayment.halfPaymentAmount is capped at what's actually
    // applied/recorded against the bill (makePayment's
    // Math.min(remaining, billPayableAmount) - a payment record can never
    // exceed what's owed), so it silently equals the bill total whenever
    // cash tendered >= bill, printing the bill amount instead of what the
    // customer actually handed over. Prefer the raw tendered amount
    // (cashTenderedRef) for CASH instead - the same figure the on-screen
    // "Cash Received"/"Change Due" confirmation (CashChangeModal) already
    // shows the cashier from this same checkout. Only reliable for a
    // single-bill checkout: cashTenderedRef/cashBillRef track the WHOLE
    // checkout's combined cash tendered/owed, not any one bill's share, so
    // a multi-bill checkout (one payment split across several bills) keeps
    // the existing capped-amount behaviour instead of printing the whole
    // checkout's tendered total on each individual bill's receipt.
    const isSingleBillCashCheckout =
      paidBy === PaymentTypeEnum.CASH && paymentHalfDetails.length === 1;
    // LATER/MONTHLY submit the full bill amount as the "payment" purely so
    // the backend can track it as a deferred NOT_CLEARED charge (see
    // LaterSection/MonthlySection) - nothing was actually collected, so
    // treating halfPaymentAmount as genuinely paid printed every deferred
    // receipt's balance as $0.
    const paid = isDeferredPayment(paidBy)
      ? 0
      : isSingleBillCashCheckout
        ? cashTenderedRef.current
        : halfPayment.halfPaymentAmount;
    // Balance reconciles Total against what was actually collected - for a
    // partial/underpayment this is what's still owed; for a cash
    // overpayment (paid > total, only possible in the
    // isSingleBillCashCheckout case above) this is the change given back,
    // matching CashChangeModal's own tendered-minus-bill convention.
    const balance = Math.abs(total - paid);
    const department = (
      session.cartItems[0]?.serviceType?.categoryName ?? "DRY CLEANING"
    ).toUpperCase();
    const stamp = session.cartItems.some((i) => i.pressOnly) ? "PRESS ONLY" : undefined;

    return {
      copyType,
      invoice: bill.billId,
      department,
      dropOff: `${formatUSDate(now)} · ${formatUS12HourTime(now)}`,
      // Nothing's been picked up yet at check-in time - always "Ready By".
      pickupLabel: "Ready By",
      pickup: sessionDate?.specialDate
        ? sessionDate.specialDate.format("MMM DD, YYYY")
        : formatUSDate(now),
      pickupAfter,
      customer: formatFullName(printCustomer),
      customerId: printCustomer.id.toString(),
      customerStatus: customerStatusLetter(printCustomer.customerTypePref),
      paymentStatus: paymentStatusLetter(printCustomer.customerPayAtPref),
      cashier: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
      cashierId: user?.id ? user.id.toString() : "",
      storeName: translation.companyName,
      storeSubtitle: "Certified Environmental Dry Cleaner",
      storeAddress: translation.companyAddress,
      storePhone: translation.companyPhone,
      storeWebsite: "www.prestovalet.net",
      items,
      subtotal,
      discount,
      total,
      notes,
      paidBy,
      paid,
      balance,
      specialStamp: stamp,
      pinTagColor: session.pinTag?.color ?? undefined,
      pinTagTags: session.pinTag?.tags ?? [],
      pinTagAlias: resolvePinTagAlias(
        session.pinTag?.color,
        store.getState().pintag.getColorPintags?.body
      ),
    };
  };

  const updateHalfOrFullPayment = async (
    paymentRequest: IPaymentRequest,
    paymentModalData: IPaymentModalData
  ) => {
    // `makePayment` awaits this per-bill, in a loop, for multi-session
    // checkouts - without this `return`, the outer `async` had no `await`
    // in its body, so it resolved as soon as the thunk was merely
    // dispatched (not once the request/state-update actually finished),
    // firing every bill's payment concurrently instead of one at a time.
    return dispatch(
      newPaymentAction({
        paymentType: paymentModalData.paymentMode,
        billId: paymentRequest.billId,
        customerId: paymentRequest.customerId,
        cashierId: user?.id ?? 0,
        amount: Number(paymentModalData.paidAmount.toFixed(2)), // to fix floating-point precision errors due to js IEEE 754 standard
        chequeIdentification: paymentModalData.checkNumber,
      })
    ).then(async (res) => {
      const payload = res?.payload as BaseResponseSingle<PaymentCreateResponse>;

      // If the API returned a FAILED status (e.g. insufficient store credit),
      // show the error modal with full context instead of a bare snackbar.
      if (!payload || payload.status === "FAILED" || !payload.body) {
        const custName = `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim();
        setPaymentErrorData({
          paymentType: paymentModalData.paymentMode,
          billAmount: billTotalForErrorRef.current,
          errorMessage: payload?.message ?? undefined,
          tenderedAmount: paymentModalData.paymentMode === PaymentTypeEnum.CASH
            ? cashTenderedRef.current : undefined,
          checkNumber: checkNumberRef.current,
          outstandingAmount: store.getState().customer.customerOutstanding?.outstandingAmount,
          availableCredit: paymentModalData.paymentMode === PaymentTypeEnum.STORE_CREDIT
            ? storeCreditBalanceBeforeRef.current : undefined,
          customerName: custName,
          customerId: customer?.id,
        });
        setShowPaymentErrorModal(true);
        return;
      }

      const updatedPaymentHalfDetails =
        store.getState().cart.paymentHalfDetails;

      // Use numeric coercion to avoid Long vs number type mismatch
      const match = updatedPaymentHalfDetails.find(
        (item: IHalfPayment) => Number(item?.billId) === Number(payload?.body?.billId)
      );

      if (!match) {
        snackbar.showSnackbar("Bill not found for payment update", "error");
        return;
      }

      const updatedHalfPayment = JSON.parse(
        JSON.stringify(
          // clone deep gave unexpected behavior so that's why used this method
          updatedPaymentHalfDetails.find(
            (item: IHalfPayment) => Number(item?.billId) === Number(payload?.body?.billId)
          )
        )
      ) as IHalfPayment;
      if (updatedHalfPayment) {
        updatedHalfPayment.halfPaymentAmount += payload.body.amount;
        updatedHalfPayment.payments.push(payload.body);
        try {
          dispatch(
            CartSliceAction.setHalfPayementDetails([
              ...updatedPaymentHalfDetails.filter(
                (item: IHalfPayment) => Number(item.billId) !== Number(payload.body.billId)
              ),
              updatedHalfPayment,
            ])
          );
          snackbar.showSnackbar("Payment successful", "success");

          // Refresh customer data so the store credit balance in the UI
          // reflects the deduction that was just made.
          if (customer?.id) {
            dispatch(getCustomerByIdAction(String(customer.id)));
          }
        } catch (err: any) {
          snackbar.showSnackbar(`Failed to update payment`, "error");
        }
      } else {
        snackbar.showSnackbar(
          "Failed to find bill's saved half payment",
          "error"
        );
      }
    });
  };

  const temporaryNotes = useAppSelector(
    (state) => state.cart.temporaryBillNotes
  );
  // Bill notes already persisted to the server (e.g. added while editing an
  // existing bill, before this checkout) - checked out bills that were only
  // just created won't have any of these yet, only `temporaryNotes` above.
  const persistedBillNotes = useAppSelector((state) => state.cart.billNotes);

  const handleOnCheckout = (data: IPaymentModalData) => {
    if (!tenantId) {
      snackbar.showSnackbar("Tenant ID not found", "error");
      return;
    }
    if (!customer) {
      snackbar.showSnackbar("Customer not found", "error");
      return;
    }
    if (!paymentType) {
      snackbar.showSnackbar("Please select a payment method", "error");
      return;
    }

    const totalBillAmount = cartTotal - totalDiscountAmount;
    billTotalForErrorRef.current = totalBillAmount;
    const isFullPaymentDone = paidAmount >= totalBillAmount;

    // Capture the tendered / bill amounts now so the change modal can use
    // them. Uses rawPaidAmount (the actual typed/stepper Cash figure), not
    // paidAmount, which payment.tsx may have inflated by `trackrem` (the
    // bill's already-collected amount) for the backend's own settlement
    // math - that inflation would otherwise make "Cash Received"/"Change
    // Due" wrong by whatever was already paid on this bill before checkout.
    if (data.paymentMode === PaymentTypeEnum.CASH) {
      cashTenderedRef.current = data.rawPaidAmount ?? data.paidAmount;
      cashBillRef.current = totalBillAmount;
    }
    if (data.paymentMode === PaymentTypeEnum.CHECK) {
      checkNumberRef.current = data.checkNumber;
    }
    if (data.paymentMode === PaymentTypeEnum.STORE_CREDIT) {
      storeCreditBalanceBeforeRef.current = customer?.storeCreditBalance ?? 0;
    }

    if (isFullPaymentDone) {
      snackbar.showSnackbar("Payment already made", "warning");
    }

    setIsCheckoutTriggered(true);

    if (paymentHalfDetails.length === 0) {
      const switchesAreGood: boolean = cart.every((session) => {
        const sessionSwitch = sessionSwitches.find(
          (switchItem) => switchItem.id === session.sessionId
        );
        return sessionSwitch?.checked !== undefined;
      });
      const datesAreGood: boolean = cart.every((session) => {
        const sessionDate = sessionDates.find(
          (dateItem) => dateItem.id === session.sessionId
        );
        return sessionDate?.specialDate !== undefined;
      });
      const timesAreGood: boolean = cart.every((session) => {
        const sessionTime = sessionTimes.find(
          (timeItem) => timeItem.id === session.sessionId
        );
        return sessionTime?.specialTime !== undefined;
      });

      if (!switchesAreGood || !datesAreGood || !timesAreGood) {
        snackbar.showSnackbar("Pickup details not found", "error");
        console.log("sessionSwitches", sessionSwitches);
        console.log("sessionDates", sessionDates);
        console.log("sessionTimes", sessionTimes);
      } else {
        console.log({
          tenant: tenantId,
          paymentType,
          sessionList: cart.map((session) => ({
            ...session,
            discount: {
              amount: getSessionDiscountById(session.sessionId),
              id: customer.discount ? Number(customer.discount) : null,
            },
            // discount: {
            //   amount: totalDiscountAmount,
            //   id: customer.discount ? Number(customer.discount) : null,
            // },
          })),
        });
        dispatch(
          newBillAction({
            tenant: tenantId,
            paymentType,
            sessionList: cart.map((session) => ({
              ...{
                ...session,
                pickupDetails: {
                  isSpecial: !!sessionSwitches.find(
                    (item) => item.id === session.sessionId
                  )?.checked,

                  // due date is combination of session date and session time
                  dueDate: (() => {
                    // const specialDate = sessionDates.find(
                    //   (item) => item.id === session.sessionId
                    // )?.specialDate;

                    const specialDate =
                      sessionDates.find((item) => item.id === session.sessionId)
                        ?.specialDate || globalSpecialDate;

                    const specialTime = sessionTimes.find(
                      (item) => item.id === session.sessionId
                    )?.specialTime;

                    if (specialDate && specialTime) {
                      return dayjs(specialDate)
                        .set("hour", specialTime.hour())
                        .set("minute", specialTime.minute())
                        .toISOString();
                    }

                    return new Date().toISOString(); // Handle undefined case appropriately
                  })(),
                },
              },
              discount: {
                amount: getSessionDiscountById(session.sessionId),
                id: customer.discount ? Number(customer.discount) : null,
              },
              // discount: {
              //   amount: totalDiscountAmount,
              //   id: customer.discount ? Number(customer.discount) : null,
              // },
            })),
          })
        ).then(async (res) => {
          const payload = res?.payload as IBillResponse[];

          if (!payload || payload.length === 0) {
            snackbar.showSnackbar("Failed to create bills.", "error");
            return;
          }

          // Freshest possible snapshot for the print payload built later in
          // this same completion flow - see justCreatedBillsRef's own
          // comment for why this can't just re-read redux `bills`/`cart`.
          justCreatedBillsRef.current = payload;
          justCreatedSessionsRef.current = cart;

          // setup slot for half payment in redux
          const halfPaymentDetails = [];
          for (const bill of payload) {
            halfPaymentDetails.push({
              fullpaymentAmount: Number(bill.billAmount.toFixed(2)),
              halfPaymentAmount: 0,
              billId: bill.id,
              customerId: customer.id,
              payments: [],
            });
          }

          dispatch(CartSliceAction.setHalfPayementDetails(halfPaymentDetails));

          makePayment(
            data.paymentMode,
            data.paidAmount,
            customer.id,
            halfPaymentDetails,
            data.checkNumber,
            data.isStoreCredit
          );

          initialCheckDone.current = true;

          // Add Bill notes for the bill
          temporaryNotes.forEach((tempNote: any) => {
            const noteBill = payload.find(
              (bill: any) => bill.sessionId === tempNote.sessionId
            );
            if (noteBill?.id) {
              dispatch(
                addBillNoteAction({
                  note: tempNote.note,
                  occasions: tempNote.occasions,
                  merchantUserId: tempNote.merchantUserId,
                  customerId: tempNote.customerId,
                  billId: noteBill.id,
                })
              )
                .then(() => {
                  snackbar.showSnackbar("Note added successfully!", "success");

                  dispatch(
                    CartSliceAction.setTemporaryBillNotes(
                      temporaryNotes.filter(
                        (note: any) => note.tempId !== tempNote.tempId
                      )
                    )
                  );
                })
                .catch((err: any) => {
                  snackbar.showSnackbar(`Failed to add notes: ${err}`, "error");
                });
            }
          });
        });
        // console.log({
        //   tenant: tenantId,
        //   paymentType,
        //   sessionList: cart.map((session) => ({
        //     ...session,
        //     discount: {
        //       amount: getSessionDiscountById(session.sessionId),
        //       id: customer.discount ? Number(customer.discount) : null,
        //     },
        //     // discount: {
        //     //   amount: totalDiscountAmount,
        //     //   id: customer.discount ? Number(customer.discount) : null,
        //     // },
        //   })),
        // });
      }
    } else {
      makePayment(
        data.paymentMode,
        data.paidAmount,
        customer.id,
        paymentHalfDetails,
        undefined,
        data.isStoreCredit
      );
    }
  };

  const handleOnUpdateBill = async () => {
    if (!tenantId) {
      snackbar.showSnackbar("Tenant ID not found", "error");
      return;
    }
    if (!customer) {
      snackbar.showSnackbar("Customer not found", "error");
      return;
    }
    if (!selectedSession) {
      snackbar.showSnackbar("Session not found", "error");
      return;
    }

    dispatch(
      editBillAction({
        tenant: tenantId,
        // Edit-bill never goes through the Payment step (see
        // handleOnFinishSummary), so paymentType is always null here now that
        // there's no default selection - the thunk's actual request body
        // hardcodes CASH regardless of what's passed in, so this is a type
        // formality, not a real value.
        paymentType: paymentType ?? PaymentTypeEnum.CASH,
        session: {
          ...{
            ...selectedSession,
            pickupDetails: {
              isSpecial: !!sessionSwitches.find(
                (item) => item.id === selectedSession.sessionId
              )?.checked,

              // due date is combination of session date and session time
              dueDate: (() => {
                // const specialDate = sessionDates.find(
                //   (item) => item.id === selectedSession.sessionId
                // )?.specialDate;

                const specialDate =
                  sessionDates.find(
                    (item) => item.id === selectedSession.sessionId
                  )?.specialDate || globalSpecialDate;

                const specialTime = sessionTimes.find(
                  (item) => item.id === selectedSession.sessionId
                )?.specialTime;

                if (specialDate && specialTime) {
                  return dayjs(specialDate)
                    .set("hour", specialTime.hour())
                    .set("minute", specialTime.minute())
                    .toISOString();
                }

                return new Date().toISOString(); // Handle undefined case appropriately
              })(),
            },
          },
          discount: {
            amount: getSessionDiscountById(selectedSession.sessionId),
            id: customer.discount ? Number(customer.discount) : null,
          },
          // discount: {
          //   amount: totalDiscountAmount,
          //   id: customer.discount ? Number(customer.discount) : null,
          // },
        },
        id: Number(billId),
      })
    ).then(async (res) => {
      const payload = res?.payload as IBillResponse[];

      if (!payload || payload.length === 0) {
        snackbar.showSnackbar("Failed to create bills.", "error");
        return;
      }
      // Add Bill notes for the bill
      temporaryNotes.forEach((tempNote: any) => {
        const noteBill = payload.find(
          (bill: any) => bill.sessionId === tempNote.sessionId
        );
        if (noteBill?.id) {
          dispatch(
            addBillNoteAction({
              note: tempNote.note,
              occasions: tempNote.occasions,
              merchantUserId: tempNote.merchantUserId,
              customerId: tempNote.customerId,
              billId: noteBill.id,
            })
          )
            .then(() => {
              snackbar.showSnackbar("Note added successfully!", "success");

              dispatch(
                CartSliceAction.setTemporaryBillNotes(
                  temporaryNotes.filter(
                    (note: any) => note.tempId !== tempNote.tempId
                  )
                )
              );
            })
            .catch((err: any) => {
              snackbar.showSnackbar(`Failed to add notes: ${err}`, "error");
            });
        }
      });

      // An edit can add items, which mints brand-new BillItem ids. Anything
      // that later pays for this bill resolves its `itemIds` from the cached
      // billItemIdList (see PickupModal.resolveBillItemIds), so leaving the
      // pre-edit list in the store means the payment is either scoped to a
      // short id list - and rejected by the backend's item-scoped balance
      // guard as exceeding the outstanding balance - or fails outright with
      // "Bill item ID not found". Refresh it here so no route back to this
      // bill can pick up the stale one.
      if (billId) {
        dispatch(getBillItemIdListAction(Number(billId)));
      }

      snackbar.showSnackbar("Bill updated successfully", "success");
      dispatch(CartSliceAction.clearSession());
      dispatch(DiscountSliceAction.resetDiscount());
      setTimeout(() => {
        navigate(`/pos/customer/${customerId}/history`);
      }, 10);
    });
  };

  const makePayment = async (
    paymentType: PaymentTypeEnum,
    totalPaid: number,
    customerId: number,
    halfPaymentProp: IHalfPayment[],
    checkNumber?: string,
    isStoreCredit?: boolean
  ) => {
    let remaining = totalPaid;

    for (const bill of halfPaymentProp) {
      if (remaining <= 0) break;

      const billPayableAmount = bill.fullpaymentAmount - bill.halfPaymentAmount;
      // To Do: discount amount should be considered here

      if (bill.fullpaymentAmount <= bill.halfPaymentAmount) continue;

      const paymentAmount = Math.min(remaining, billPayableAmount);

      const paymentRequest: IPaymentRequest = {
        paymentType: paymentType,
        billId: bill.billId!,
        customerId: customerId,
        cashierId: user?.id ?? 0,
        amount: Number(paymentAmount.toFixed(2)), // to fix floating-point precision errors due to js IEEE 754 standard
      };

      await updateHalfOrFullPayment(paymentRequest, {
        paymentMode: paymentType,
        paidAmount: paymentAmount,
        checkNumber: checkNumber,
        isStoreCredit: isStoreCredit,
      });

      remaining -= paymentAmount;
    }
  };

  useEffect(() => {
    if (open) {
      hasMounted.current = false;
      modalShownRef.current = false;
      setIntiaLRender(true);
      dispatch(CartSliceAction.setHalfPayementDetails([]));
      // A fresh check-in must not print off whatever the PREVIOUS
      // customer's newBillAction resolution left in these refs, for the
      // (rare) case this same modal instance pays an existing bill without
      // going through that create call again.
      justCreatedBillsRef.current = null;
      justCreatedSessionsRef.current = null;
    }
  }, [open]);

  const handleOnClose = () => {
    onClose();
  };

  useEffect(() => {
    return () => {
      setContent(PaymentModalContents.SUMMARY);
    };
  }, [open]);

  const initialCheckDone = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (!initialCheckDone.current) return;

    const isPaymentComplete =
      Number(paidAmount.toFixed(2)) >=
      Number(totalBillAmountFromHalfPayment.toFixed(2));

    if (isPaymentComplete && !modalShownRef.current) {
      modalShownRef.current = true;

      // Snapshot print payloads before cart is cleared. Prefer the
      // just-created-bills ref (set the instant newBillAction resolved,
      // see its own comment) over the redux `bills` selector, which by this
      // point may be several dispatches removed from when it was actually
      // populated - reading it here was previously found to silently yield
      // zero print requests with none of the more granular fallbacks below
      // ever triggering, meaning the array itself (not any one lookup) was
      // already empty/stale.
      const billsForPrint = justCreatedBillsRef.current ?? bills;
      // customerRef sidesteps getCustomerByIdAction's post-payment refresh
      // (dispatched by updateHalfOrFullPayment) wiping state.customer.customer
      // to null for the GET's duration - see customerRef's own comment.
      const customerForPrint = customerRef.current ?? customer;
      const printRequests: ITrayPrintRequest[] = [];
      if (!billsForPrint || billsForPrint.length === 0 || !customerForPrint) {
        console.warn(
          `[payment-modal] Skipping print snapshot - bills: ${billsForPrint?.length ?? "none"}, customer: ${customerForPrint ? "present" : "missing"}.`
        );
      } else {
        for (const bill of billsForPrint) {
          const halfPayment = paymentHalfDetails.find(
            (hp) => Number(hp.billId) === bill.id
          );
          if (!halfPayment) {
            console.warn(
              `[payment-modal] No half-payment entry found for bill ${bill.id} - dropping its print request.`
            );
            continue;
          }
          const storeCopy = buildPrintRequestForBill(bill, halfPayment, "storeCopy");
          const receipt = buildPrintRequestForBill(bill, halfPayment, "receipt");
          if (storeCopy) printRequests.push(storeCopy);
          if (receipt) printRequests.push(receipt);
        }
      }
      console.warn(
        `[payment-modal] Print snapshot built ${printRequests.length} request(s) for ${billsForPrint?.length ?? 0} bill(s).`
      );

      onClose();

      dispatch(CartSliceAction.clearSession());
      dispatch(DiscountSliceAction.resetDiscount());
      dispatch(
        addSession({
          sessionId: uuidv4(),
          cartItems: [],
          customerId: userId,
          pinTag: {
            color: null,
            tags: [],
            date: null,
          },
        })
      );

      setPendingPrintRequests(printRequests);
      setPrintingCopy(null);
      setPrintFeedback(null);

      // Build confirmation modal data based on payment type
      const custName = `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim();
      const latestBill = bills?.[0];

      switch (paymentType) {
        case PaymentTypeEnum.CASH: {
          const tendered = cashTenderedRef.current;
          const bill = cashBillRef.current;
          setCashChangeData({
            paymentType,
            billAmount: bill,
            tenderedAmount: tendered,
            changeAmount: Math.max(0, tendered - bill),
            isStoreCredit: false,
            customerName: custName,
            customerId: customer?.id,
          });
          break;
        }
        case PaymentTypeEnum.CHECK:
          setCashChangeData({
            paymentType,
            billAmount: totalBillAmountFromHalfPayment,
            checkNumber: checkNumberRef.current,
            customerName: custName,
            customerId: customer?.id,
          });
          break;
        case PaymentTypeEnum.MONTHLY:
          setCashChangeData({
            paymentType,
            billAmount: totalBillAmountFromHalfPayment,
            outstandingAmount: store.getState().customer.customerOutstanding?.outstandingAmount,
            customerName: custName,
            customerId: customer?.id,
          });
          break;
        case PaymentTypeEnum.LATER:
          setCashChangeData({
            paymentType,
            billAmount: totalBillAmountFromHalfPayment,
            outstandingAmount: store.getState().customer.customerOutstanding?.outstandingAmount,
            dueDate: (latestBill?.pickupDetails as any)?.dueDate,
            invoiceId: latestBill?.billId ?? latestBill?.id?.toString(),
            customerName: custName,
            customerId: customer?.id,
          });
          break;
        case PaymentTypeEnum.STORE_CREDIT:
          setCashChangeData({
            paymentType,
            billAmount: totalBillAmountFromHalfPayment,
            previousStoreCreditBalance: storeCreditBalanceBeforeRef.current,
            customerName: custName,
            customerId: customer?.id,
          });
          break;
        default: // CARD and any future types
          setCashChangeData({
            paymentType: paymentType ?? undefined,
            billAmount: totalBillAmountFromHalfPayment,
            customerName: custName,
            customerId: customer?.id,
          });
      }
      setShowCashChangeModal(true);
    }
  }, [paidAmount, paymentHalfDetails]);

  useEffect(() => {
    if (billsError) {
      setModalActionItems(failureActionItems);
      onClose();
      setOpenActionTileModal(true);
      snackbar.showSnackbar(`Order Failed: ${billsError}`, "error");
    }
  }, [billsError]);

  const contentNames = {
    summary: "Check in",
    payment: "Payment",
    success: "Payment",
    failure: "Payment",
  };

  const handleOnFinishSummary = () => {
    if (!billId) {
      setContent(PaymentModalContents.PAYMENT);
    } else {
      handleOnUpdateBill();
    }
  };

  const handleOnPayment = (data: IPaymentModalData) => {
    handleOnCheckout(data);
  };

  const handleContinue = () => {
    onClose();
  };

  const handlePrintCopySelection = (copy: PrintCopyChoice) => {
    // "none" fires when the modal is closing (Done / X) — clear the snapshot, nothing to print.
    if (copy === "none") {
      setPendingPrintRequests([]);
      setPrintingCopy(null);
      setPrintFeedback(null);
      return;
    }

    // Ignore clicks while a print job is already in flight so the cashier can't
    // fire overlapping jobs at the printer by double-clicking or hitting two buttons.
    if (printingCopy) return;

    const filtered =
      copy === "both" ? pendingPrintRequests :
      pendingPrintRequests.filter((r) => r.copyType === copy);

    console.warn('Filtered print requests for copy type', copy, filtered);

    // Surface this instead of a silent no-op - a cashier who presses a
    // Print button and sees nothing happen has no way to tell "already
    // printed, nothing left to do" apart from "this is broken".
    if (filtered.length === 0) {
      setPrintFeedback({
        copy,
        success: false,
        message: "Nothing to print for this receipt.",
      });
      return;
    }

    setPrintingCopy(copy);
    setPrintFeedback(null);

    (async () => {
      let failures = 0;
      for (const req of filtered) {
        const ok = await printTicket(req);
        if (!ok) failures++;
      }
      setPrintingCopy(null);
      setPrintFeedback({
        copy,
        success: failures === 0,
        message:
          failures === 0
            ? undefined
            : failures === filtered.length
              ? "Could not reach the printer. Make sure Presto Tray is running on this computer."
              : `${failures} of ${filtered.length} copies failed to print.`,
      });
      // Keep pendingPrintRequests intact so the cashier can retry a failed copy,
      // or print the other copy type afterwards — only "Done" clears the snapshot.
    })();
  };

  const handleOnPaymentTypeChange = (paymentType: PaymentTypeEnum) => {
    setPaymentType(paymentType);
  };

  const sessionInfo = cart.map((session) => ({
    sessionId: session.sessionId,
    date: session.pinTag?.date ?? "No date",
  }));

  const { date: specialDate } = useSpecialDate();

  useEffect(() => {
    if (!sessionInfo || sessionInfo.length === 0) return;

    const sessionDates = sessionInfo.map((item) => item.date);

    const allEqualToSpecial = sessionDates.every(
      (date) => date === specialDate?.format("YYYY-MM-DD")
    );

    setMasterSwitch(allEqualToSpecial);
  }, [sessionInfo, specialDate]);

  const contentComponents = {
    summary: (
      <CheckInSummary
        onCheckout={handleOnFinishSummary}
        masterSwitch={masterSwitch}
        sessionSwitches={sessionSwitches}
        masterDate={masterDate}
        masterTime={masterTime}
        sessionDates={sessionDates}
        sessionTimes={sessionTimes}
        isModalOpen={open}
        handleOnChangeMasterSwitch={handleOnChangeMasterSwitch}
        handleOnChangeMasterDate={handleOnChangeMasterDate}
        handleOnChangeMasterTime={handleOnChangeMasterTime}
        handleOnChangeSessionSwitch={handleOnChangeSessionSwitch}
        handleOnChangeSessionDate={handleOnChangeSessionDate}
        handleOnChangeSessionTime={handleOnChangeSessionTime}
      />
    ),
    payment: (
      <Payment
        onCheckout={handleOnPayment}
        onPaymentTypeChange={handleOnPaymentTypeChange}
        paymentType={paymentType}
        requireManagerApprovalForLater={false}
      />
    ),
    success: <Success onContinue={handleContinue} />,
    failure: <Failure onContinue={handleContinue} />,
  };

  const storeCreditBalance = customer?.storeCreditBalance ?? 0;

  const storeCreditTitleRight = content === PaymentModalContents.PAYMENT ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        backgroundColor: "#e8f5e9",
        borderRadius: "2rem",
        px: "1rem",
        py: "0.35rem",
        ml: "auto",
        mr: "0.75rem",
      }}
    >
      <AccountBalanceWallet sx={{ color: "#2e7d32", fontSize: "1.4rem" }} />
      <Typography
        sx={{
          color: "#2e7d32",
          fontWeight: 600,
          fontSize: "1.14285714rem",
          whiteSpace: "nowrap",
        }}
      >
        Store Credit Balance : ${storeCreditBalance.toFixed(2)}
      </Typography>
    </Box>
  ) : undefined;

  return (
    <>
      <CustomModal
        title={contentNames[content]}
        openModal={open}
        width="95.0714286rem"
        height="64.7857143rem"
        onClose={handleOnClose}
        titleRight={storeCreditTitleRight}
      >
        {contentComponents[content]}
      </CustomModal>

      <ActionTileModal
        openModal={openActionTileModal}
        onClose={() => setOpenActionTileModal(false)}
        actionItems={modalActionItems}
      />

      {cashChangeData && (
        <CashChangeModal
          open={showCashChangeModal}
          onClose={() => {
            setShowCashChangeModal(false);
            setCashChangeData(null);
            setModalActionItems(successActionItems);
            setOpenActionTileModal(true);
          }}
          paymentType={cashChangeData.paymentType}
          billAmount={cashChangeData.billAmount}
          tenderedAmount={cashChangeData.tenderedAmount}
          changeAmount={cashChangeData.changeAmount}
          isStoreCredit={cashChangeData.isStoreCredit}
          checkNumber={cashChangeData.checkNumber}
          outstandingAmount={cashChangeData.outstandingAmount}
          dueDate={cashChangeData.dueDate}
          previousStoreCreditBalance={cashChangeData.previousStoreCreditBalance}
          customerName={cashChangeData.customerName}
          customerId={cashChangeData.customerId}
          invoiceId={cashChangeData.invoiceId}
          onPrintSelect={handlePrintCopySelection}
          printingCopy={printingCopy}
          printFeedback={printFeedback}
        />
      )}

      {paymentErrorData && (
        <PaymentErrorModal
          open={showPaymentErrorModal}
          onClose={() => {
            setShowPaymentErrorModal(false);
            setPaymentErrorData(null);
          }}
          onRetry={() => {
            setShowPaymentErrorModal(false);
            setPaymentErrorData(null);
          }}
          {...paymentErrorData}
        />
      )}
    </>
  );
};

export default PaymentModal;
